/*
 * -----------------------------------------------------------------------
 *  <copyright file="edit-study-site-overview-config/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import { TabsContext } from '@components/admin/studies/study-settings-dialogs/edit-overview-tables';
import {
    formSiteOverviewData,
    formStudyOverviewData,
} from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/edit-study-site-overview-config/edit-study-site-overview-config.helper';
import {
    EditOverviewConfigProps,
    IConfigurationInput,
    IDisabledFields,
    IStudyOverviewData,
    IStudySiteOverviewData,
    StudyOverviewTableConfigurationColumnConfigurations,
    StudySiteOverviewTableConfigurationColumnConfigurations,
    // eslint-disable-next-line max-len
} from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/edit-study-site-overview-config/edit-study-site-overview-config.interface';
import PanelsOverviewGrid from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/panels-overview-grid';
import RowOrder from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/row-order';
import TabHeading from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/tab-heading';
import CoreConfirmationDialog from '@components/core/core-confirmation-dialog';
import DarkTooltip from '@components/core/dark-tool-tip/dark-tool-tip';
import DialogInformationBox from '@components/core/dialog-information-box/dialog-information-box';
import {
    InfoBoxIcons,
    InfoBoxStyleClasses,
} from '@components/core/dialog-information-box/dialog-information-box.interface';
import EditableGrid from '@components/core/editable-grid';
import { EditableGridCellEditor } from '@components/core/editable-grid/editable-grid.interface';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import { SelectWithSearchDataArrayInterface } from '@components/core/select-with-categories/select-with-categories.interface';
import {
    OverviewColumnConfigurationEnumType,
    RowDataSourceTable,
    StudyOverviewTableConfigurationInputType,
    StudySiteOverviewTableConfigurationInputType,
    StudyOverviewColumnConfigurationInput,
} from 'types/graphql-global-types';
import { useBoolean, useLocalState, useTranslation } from '@hooks/index';
import { isFieldType, isNumericField, isSiteField, isStudyField } from '@utils/field-helpers';
import { createArrayFromEnum } from '@utils/generator';
import { gridsHeightCalculating } from '@utils/grid-v2';
import NotificationService from '@utils/notification';
import { checkIfNameExist, checkRequiredFields } from '@utils/validation';
import {
    FieldDefinitionFields,
    // eslint-disable-next-line max-len
    StudyFields_Study_studyOverviewTableConfiguration_StudyOverviewTableConfigurationType_columnConfigurations_StudyOverviewColumnConfiguration as StudyOverviewColumnConfiguration,
    // eslint-disable-next-line max-len
    StudyFields_Study_studyOverviewTableConfiguration_StudyOverviewTableConfigurationType_panelConfigurations_StudyOverviewPanelConfigurations as StudyOverviewPanelConfigurations,
    // eslint-disable-next-line max-len
    StudyFields_Study_studySiteOverviewTableConfiguration_StudySiteOverviewTableConfigurationType_panelConfigurations_StudyOverviewPanelConfigurations as StudySiteOverviewPanelConfigurations,
    // eslint-disable-next-line max-len
    StudyFields_Study_studySiteOverviewTableConfiguration_StudySiteOverviewTableConfigurationType_columnConfigurations_StudySiteOverviewColumnConfiguration as StudySiteOverviewColumnConfiguration,
} from '@queries/generated/fragments.generated';
import {
    saveStudyOverviewTableConfiguration,
    saveStudyOverviewTableConfigurationVariables,
    saveStudySiteOverviewTableConfiguration,
    saveStudySiteOverviewTableConfigurationVariables,
} from '@queries/generated/studies.generated';
import {
    GET_STUDY,
    SAVE_STUDY_OVERVIEW_TABLE_CONFIGURATION,
    SAVE_STUDY_SITE_OVERVIEW_TABLE_CONFIGURATION,
} from '@queries/studies';

const EditStudySiteOverviewConfig = ({ study, canEdit, activeTab, isStudy }: EditOverviewConfigProps) => {
    const { t } = useTranslation();
    const initialState = isStudy ? formStudyOverviewData(study) : formSiteOverviewData(study);
    const currentOverviewConfigurationType = isStudy
        ? 'studyOverviewTableConfigurationInputType'
        : 'studySiteOverviewTableConfigurationInputType';
    const { state: localState } = useLocalState();
    const disabledSaveUndoButtons = useBoolean(true);
    const hasErrorsDialog = useBoolean(false);
    const [state, setState] = useState<IStudyOverviewData | IStudySiteOverviewData>({ ...initialState });
    const [errors, setErrors] = useState({} as any);
    const [disabledFields, setDisabledFields] = useState<IDisabledFields[]>([]);
    const tabsContext = useContext(TabsContext);
    const [seriesRowDataOptions, setSeriesRowDataOptions] = useState<SelectWithSearchDataArrayInterface[]>([]);
    const gridsWrapRef = useRef(null);
    const [screenHeight, setScreenHeight] = useState(window.innerHeight);

    const handleResize = () => {
        setScreenHeight(window.innerHeight);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (gridsWrapRef.current) {
            gridsHeightCalculating(gridsWrapRef, 2, 80.5, isStudy ? 20 : 84);
        }
    }, [screenHeight]);

    useEffect(() => {
        setSeriesRowDataOptions(
            createArrayFromEnum(
                RowDataSourceTable,
                '_TABLE',
                null,
                (a: RowDataSourceTable, b: RowDataSourceTable) =>
                    a === RowDataSourceTable.STUDY ? -1 : b === RowDataSourceTable.STUDY ? 1 : 0,
                true,
            ),
        );
    }, []);

    useEffect(() => {
        const getConfigurations = (configurations: StudyOverviewColumnConfigurationInput[]) =>
            JSON.stringify(
                configurations?.map(
                    ({
                        calculation,
                        dataSource,
                        fields,
                        name,
                        order,
                        type,
                    }: StudyOverviewColumnConfigurationInput) => ({
                        calculation,
                        dataSource: dataSource !== 'NONE' ? dataSource : null,
                        fields,
                        name,
                        order,
                        type,
                    }),
                ),
            );

        const stateColumns = getConfigurations(state[currentOverviewConfigurationType]?.columnConfigurations);
        const statePanels = JSON.stringify(state[currentOverviewConfigurationType]?.panelConfigurations);
        const initialStateColumns = getConfigurations(
            initialState[currentOverviewConfigurationType]?.columnConfigurations,
        );
        const initialStatePanels = JSON.stringify(initialState[currentOverviewConfigurationType]?.panelConfigurations);

        const stateRowOrder = state[currentOverviewConfigurationType]?.rowOrder;
        const initialStateRowOrder = initialState[currentOverviewConfigurationType]?.rowOrder;

        const checkboxChanged = isStudy
            ? initialState.displayStudyOverviewOnStudySummary !== state.displayStudyOverviewOnStudySummary
            : initialState.displayStudySiteOverviewOnStudySummary !== state.displayStudySiteOverviewOnStudySummary;

        const hasChanges =
            stateColumns !== initialStateColumns ||
            statePanels !== initialStatePanels ||
            stateRowOrder !== initialStateRowOrder ||
            checkboxChanged;

        tabsContext.setChangesContain(hasChanges);
        disabledSaveUndoButtons.setValue(!hasChanges);
    }, [state]);

    useEffect(() => {
        setState({ ...initialState }); // reset unsaved changes after change tab
        setErrors({});
    }, [activeTab]);

    const [saveStudyTableConfiguration] = useMutation<
        saveStudyOverviewTableConfiguration,
        saveStudyOverviewTableConfigurationVariables
    >(SAVE_STUDY_OVERVIEW_TABLE_CONFIGURATION);

    const [saveStudySiteTableConfiguration] = useMutation<
        saveStudySiteOverviewTableConfiguration,
        saveStudySiteOverviewTableConfigurationVariables
    >(SAVE_STUDY_SITE_OVERVIEW_TABLE_CONFIGURATION);

    useEffect(() => {
        const singleDataItem = {
            type: null,
            calculation: null,
            dataSource: null,
            fields: null,
            name: t('admin:defaultPanelColumns'),
            order: 1,
            default: true,
        };

        const newData = {
            ...state,
            [currentOverviewConfigurationType]: {
                ...state[currentOverviewConfigurationType],
                columnConfigurations: [singleDataItem],
            },
        };

        const isDefaultColumnOnly = state[currentOverviewConfigurationType]?.columnConfigurations?.find(
            (columnConfig: any) =>
                columnConfig.inEdit &&
                !columnConfig.type &&
                !columnConfig.calculation &&
                !columnConfig.dataSource &&
                !columnConfig.fields &&
                columnConfig.name === t('admin:defaultPanelColumns'),
        );

        const isSingleRow =
            state[currentOverviewConfigurationType]?.columnConfigurations?.length === 0 ||
            (state[currentOverviewConfigurationType]?.columnConfigurations?.length === 1 && isDefaultColumnOnly);

        const stateData = isStudy ? formStudyOverviewData(study) : formSiteOverviewData(study);
        setState(isSingleRow ? newData : stateData);
    }, [study]);

    const onChangeCheckbox = (fieldName: keyof IStudySiteOverviewData) => () => {
        disabledSaveUndoButtons.setFalse();
        setState({ ...state, [fieldName]: !state[fieldName] });
    };

    const onUndo = () => {
        disabledSaveUndoButtons.setTrue();
        setState(initialState);
    };

    const successRequest = () => {
        disabledSaveUndoButtons.setTrue();
        NotificationService.showNotification(t('admin:studySuccessfulUpdated', { id: study.recordId }));
    };

    const errorRequest = () => {
        disabledSaveUndoButtons.setFalse();
        NotificationService.showNotification(t('errorMessage'));
    };

    useEffect(() => {
        if (errors && Object.keys(errors).filter((item: string) => errors[item]).length) {
            disabledSaveUndoButtons.setTrue();
        }
    }, [errors]);

    const requiredCheck = (
        fields: string[],
        stateVal?:
            | StudyOverviewTableConfigurationColumnConfigurations
            | StudySiteOverviewTableConfigurationColumnConfigurations,
    ) => {
        const fieldToValidate: string[] = [];
        const stateValues: { [key: string]: number | string } = {};

        const configurationInputType = state[currentOverviewConfigurationType];

        const newColumnState =
            stateVal ||
            configurationInputType?.columnConfigurations?.filter(
                (
                    item:
                        | StudyOverviewTableConfigurationColumnConfigurations
                        | StudySiteOverviewTableConfigurationColumnConfigurations,
                ) => item.inEdit,
            );

        newColumnState
            ?.flatMap(
                (
                    newState:
                        | StudyOverviewTableConfigurationColumnConfigurations
                        | StudySiteOverviewTableConfigurationColumnConfigurations,
                ) =>
                    fields.map((field: string) => {
                        let fieldValue = newState[field];

                        if (field !== 'type' && newState[field] === null) {
                            if (
                                newState.type === OverviewColumnConfigurationEnumType.CALCULATED &&
                                field === 'fields'
                            ) {
                                fieldValue = 'dummyData';
                            }

                            if (
                                (newState.type === OverviewColumnConfigurationEnumType.OVERVIEW ||
                                    newState.type === OverviewColumnConfigurationEnumType.STATIC) &&
                                (field === 'dataSource' || field === 'calculation')
                            ) {
                                fieldValue = 'dummyData';
                            }
                            if (
                                newState.type === null &&
                                (field === 'dataSource' || field === 'calculation' || field === 'fields')
                            ) {
                                fieldValue = 'dummyData';
                            }
                        }

                        const key = `${field}-${newState.order}`;
                        fieldToValidate.push(key);
                        return [key, fieldValue];
                    }),
            )
            .forEach(([key, value]: [string, string | number]) => {
                stateValues[key] = value;
            });

        return checkRequiredFields({ fields: fieldToValidate, state: stateValues, setErrors, errors });
    };

    const existNameValidate = (
        columnConfigurations:
            | StudyOverviewTableConfigurationColumnConfigurations[]
            | StudySiteOverviewTableConfigurationColumnConfigurations[]
            | null,
    ) => {
        let isInvalidNames = false;
        let nameErrors = {};
        columnConfigurations?.forEach((item: IConfigurationInput) => {
            if (item.inEdit) {
                const isColumnNameExist = checkIfNameExist(item, columnConfigurations, 'name', 'order');

                if (isColumnNameExist) {
                    nameErrors = {
                        ...nameErrors,
                        [`name-${item.order}`]: t('admin:nameAlreadyExist'),
                    };
                    isInvalidNames = true;
                }
            }
        });

        if (Object.keys(nameErrors).length) {
            setErrors({ ...errors, ...nameErrors });
        }

        return isInvalidNames;
    };

    const onSave = () => {
        disabledSaveUndoButtons.setTrue();
        if (requiredCheck(['name', 'type', 'dataSource', 'fields', 'calculation'])) {
            const { columnConfigurations } = state[currentOverviewConfigurationType];

            if (existNameValidate(columnConfigurations)) {
                hasErrorsDialog.setTrue();
                return;
            }

            const saveStudyData = {
                variables: {
                    ...state,
                    studyOverviewTableConfigurationInputType: {
                        ...state.studyOverviewTableConfigurationInputType,
                        columnConfigurations: columnConfigurations?.map(
                            (colConfig: StudyOverviewColumnConfiguration, index: number) => ({
                                calculation: colConfig.calculation,
                                dataSource: colConfig.dataSource,
                                fields: colConfig.fields,
                                name: colConfig.name,
                                type: colConfig.type,
                                order: index + 1,
                                default: colConfig.default,
                            }),
                        ),
                        panelConfigurations: state.studyOverviewTableConfigurationInputType?.panelConfigurations?.map(
                            (panelConfig: StudyOverviewPanelConfigurations, index: number) => ({
                                panelId: panelConfig.panelId,
                                panelOrder: index + 1,
                            }),
                        ),
                    },
                    studyId: study.id,
                } as saveStudyOverviewTableConfigurationVariables,
                refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
            };

            const saveStudySiteData = {
                variables: {
                    ...state,
                    studySiteOverviewTableConfigurationInputType: {
                        ...state.studySiteOverviewTableConfigurationInputType,
                        columnConfigurations: columnConfigurations?.map(
                            (colConfig: StudySiteOverviewColumnConfiguration, index: number) => ({
                                calculation: colConfig.calculation,
                                dataSource: colConfig.dataSource,
                                fields: colConfig.fields,
                                name: colConfig.name,
                                type: colConfig.type,
                                order: index + 1,
                                default: colConfig.default,
                            }),
                        ),
                        panelConfigurations:
                            state.studySiteOverviewTableConfigurationInputType?.panelConfigurations?.map(
                                (panelConfig: StudySiteOverviewPanelConfigurations, index: number) => ({
                                    panelId: panelConfig.panelId,
                                    panelOrder: index + 1,
                                }),
                            ),
                        rowOrder: state.studySiteOverviewTableConfigurationInputType?.rowOrder,
                    },
                    studyId: study.id,
                } as saveStudySiteOverviewTableConfigurationVariables,
                refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
            };

            if (isStudy) {
                saveStudyTableConfiguration(saveStudyData)
                    .then(() => {
                        successRequest();
                    })
                    .catch(() => {
                        errorRequest();
                    });
            } else {
                saveStudySiteTableConfiguration(saveStudySiteData)
                    .then(() => {
                        successRequest();
                    })
                    .catch(() => {
                        errorRequest();
                    });
            }
        } else {
            hasErrorsDialog.setTrue();
        }
    };

    const tabActions = [
        {
            element: (
                <DarkTooltip title={t('common:undo')} placement="top" arrow>
                    <FontAwesomeIcon icon={['fal', 'undo']} size="xl" />
                </DarkTooltip>
            ),
            action: onUndo,
            disabled: disabledSaveUndoButtons.value || !canEdit,
            'data-testid': 'undo-button',
        },
        {
            element: (
                <DarkTooltip title={t('common:save')} placement="top" arrow>
                    <FontAwesomeIcon icon={['fal', 'floppy-disk']} size="xl" />
                </DarkTooltip>
            ),
            action: onSave,
            disabled: disabledSaveUndoButtons.value || !canEdit,
            'data-testid': 'save-button',
        },
    ];

    const addNew = () => {
        disabledSaveUndoButtons.setFalse();
        const { columnConfigurations } = state[currentOverviewConfigurationType];
        const newDataItem = {
            inEdit: true,
            type: null,
            calculation: null,
            dataSource: null,
            fields: null,
            name: null,
            order: columnConfigurations && columnConfigurations.length + 1,
        };

        const newColumnConfigurations = [...columnConfigurations];
        newColumnConfigurations.unshift(newDataItem);

        const newData = {
            ...state,
            [currentOverviewConfigurationType]: {
                ...state[currentOverviewConfigurationType],
                columnConfigurations: newColumnConfigurations,
            },
        };

        setState({ ...newData });
    };

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.expandMenu,
            customClassName: 'grid-header__item--always-visible',
            expandMenuItems: [
                {
                    label: 'admin:addColumn',
                    onClick: addNew,
                    icon: ['fal', 'add'],
                    enabled: true,
                },
            ],
        },
    ];

    const defineDisabledFields = (
        currentRow?:
            | StudyOverviewTableConfigurationColumnConfigurations
            | StudySiteOverviewTableConfigurationColumnConfigurations,
        isRowExist?: boolean,
    ) => {
        if (currentRow?.inEdit) {
            const copyDisabledFields = disabledFields.filter((item) => item?.order !== currentRow.order);

            if (currentRow.type === OverviewColumnConfigurationEnumType.CALCULATED) {
                copyDisabledFields.push({ field: 'fields', order: currentRow.order });
                setErrors((e) => ({
                    ...e,
                    [`fields-${currentRow.order}`]: '',
                }));
                currentRow.fields = null;
            }

            if (
                currentRow.type === OverviewColumnConfigurationEnumType.OVERVIEW ||
                currentRow.type === OverviewColumnConfigurationEnumType.STATIC
            ) {
                copyDisabledFields.push({ field: 'dataSource', order: currentRow.order });
                copyDisabledFields.push({ field: 'calculation', order: currentRow.order });
                setErrors((e) => ({
                    ...e,
                    [`dataSource-${currentRow.order}`]: '',
                    [`calculation-${currentRow.order}`]: '',
                }));
                currentRow.dataSource = null;
                currentRow.calculation = null;
            }

            setDisabledFields([...copyDisabledFields]);

            if (isRowExist) {
                const { columnConfigurations } = state[currentOverviewConfigurationType];

                const currentIndex = columnConfigurations?.findIndex(
                    (item: StudyOverviewColumnConfiguration) => item.order === currentRow.order,
                );

                if (currentIndex && currentIndex !== -1 && columnConfigurations.length) {
                    columnConfigurations[currentIndex] = currentRow;
                }

                const newState = {
                    ...state,
                    [currentOverviewConfigurationType]: {
                        ...state[currentOverviewConfigurationType],
                        columnConfigurations,
                    },
                };

                setState(newState);
            } else {
                const newState = {
                    ...state,
                    [currentOverviewConfigurationType]: {
                        ...state[currentOverviewConfigurationType],
                        columnConfigurations: {
                            ...state?.[currentOverviewConfigurationType]?.columnConfigurations,
                            ...currentRow,
                        },
                    },
                };

                setState(newState);
            }
        }
    };

    const makeDynamicFieldsList = (items: FieldDefinitionFields[]) => {
        items.sort((a, b) => `${a.dataType}.${a.name}`.localeCompare(`${b.dataType}.${b.name}`));

        const options: SelectWithSearchDataArrayInterface[] = items.map((item) => ({
            id: item.key,
            text: `${item.dataType}.${item.name}`,
        }));

        options.unshift({
            id: null,
            text: t('admin:selectValue'),
        });

        return options;
    };

    const getFieldsLovs = (
        type: OverviewColumnConfigurationEnumType | null | undefined,
    ): SelectWithSearchDataArrayInterface[] => {
        if (type === OverviewColumnConfigurationEnumType.CALCULATED || type === null || type === undefined) {
            return [
                {
                    id: null,
                    text: t('admin:selectValue'),
                },
            ];
        }
        if (type === OverviewColumnConfigurationEnumType.OVERVIEW) {
            return isStudy
                ? [
                      {
                          id: null,
                          text: t('admin:selectValue'),
                      },
                      {
                          id: 'NumRedSites',
                          text: `#${t('admin:redSites')}`,
                      },
                      {
                          id: 'NumAmberSites',
                          text: `#${t('admin:amberSites')}`,
                      },
                      {
                          id: 'NumGreenSites',
                          text: `#${t('admin:greenSites')}`,
                      },
                  ]
                : [
                      {
                          id: null,
                          text: t('admin:selectValue'),
                      },
                      {
                          id: 'NumRedKris',
                          text: `${t('cm:redKris')}`,
                      },
                      {
                          id: 'NumAmberKris',
                          text: `${t('cm:amberKris')}`,
                      },
                      {
                          id: 'NumRedDecisionKris',
                          text: `${t('cm:redDecisionKris')}`,
                      },
                      {
                          id: 'NumAmberDecisionKris',
                          text: `${t('cm:amberDecisionKris')}`,
                      },
                      {
                          id: 'Site_Score',
                          text: `${t('cm:siteScore')}`,
                      },
                  ];
        }

        const items =
            localState?.fields?.filter(
                (fld: FieldDefinitionFields) =>
                    !fld.isHidden &&
                    (isStudy ? isStudyField(fld) : isStudyField(fld) || isSiteField(fld)) &&
                    !fld.isCalculated,
            ) ?? [];

        return makeDynamicFieldsList(items);
    };

    const getCalculationLovs = (dataSource: RowDataSourceTable | null | undefined) => {
        if (dataSource === RowDataSourceTable.NONE || dataSource === null || dataSource === undefined) {
            return [
                {
                    id: null,
                    text: t('admin:selectValue'),
                },
            ];
        }

        if (dataSource === RowDataSourceTable.STUDY) {
            const items =
                localState?.fields?.filter((fld: FieldDefinitionFields) => !fld.isHidden && isStudyField(fld)) ?? [];

            return makeDynamicFieldsList(items);
        }

        if (dataSource === RowDataSourceTable.SITE) {
            const items =
                localState?.fields?.filter(
                    (fld: FieldDefinitionFields) =>
                        !fld.isHidden && isSiteField(fld) && (!isStudy || isNumericField(fld)),
                ) ?? [];

            return makeDynamicFieldsList(items);
        }

        const items =
            localState?.fields?.filter(
                (fld: FieldDefinitionFields) => !fld.isHidden && isFieldType(fld, dataSource) && isNumericField(fld),
            ) ?? [];

        return makeDynamicFieldsList(items);
    };

    const defineSelectLOVs = (field: string, order: number | null | undefined) => {
        const currentConfiguration:
            | StudyOverviewTableConfigurationInputType
            | StudySiteOverviewTableConfigurationInputType = state[currentOverviewConfigurationType];
        const currentRowFields = currentConfiguration.columnConfigurations?.find((item) => item?.order === order);

        if (field === 'fields') {
            return getFieldsLovs(currentRowFields?.type);
        }

        if (field === 'calculation') {
            return getCalculationLovs(currentRowFields?.dataSource);
        }

        return [];
    };

    return (
        <div>
            {study.displayStudyOverviewOnStudySummary &&
                !study.studyOverviewTableConfiguration?.columnConfigurations?.length && (
                    <DialogInformationBox
                        topText={t('admin:studyOverviewTablesEmptyColumnsWarning')}
                        boxStyleClass={InfoBoxStyleClasses.amber}
                        icon={InfoBoxIcons.exclamationTriangle}
                    />
                )}
            {hasErrorsDialog.value && (
                <CoreConfirmationDialog
                    open={hasErrorsDialog.value}
                    close={() => hasErrorsDialog.setFalse()}
                    maxWidth="md"
                    title={t('admin:fieldErrorS')}
                    mainText={t('admin:overviewFieldsErrorMainText')}
                    additionalText={t('admin:overviewFieldsErrorAdditionalText')}
                    hideButtons
                />
            )}
            <TabHeading
                id={isStudy ? 'study-overview-study-summary-display' : 'site-overview-study-summary-display'}
                displayValue={
                    isStudy ? state.displayStudyOverviewOnStudySummary : state.displayStudySiteOverviewOnStudySummary
                }
                setDisplay={onChangeCheckbox(
                    isStudy ? 'displayStudyOverviewOnStudySummary' : 'displayStudySiteOverviewOnStudySummary',
                )}
                actions={tabActions}
                key={isStudy ? 'study-heading' : 'site-heading'}
            />
            <div className="grids-wrapper" ref={gridsWrapRef}>
                <EditableGrid
                    customKey="columns-reorder-grid"
                    className={cn('column-grid', { study: isStudy, site: !isStudy })}
                    items={state?.[currentOverviewConfigurationType]?.columnConfigurations}
                    columns={[
                        {
                            label: `${t('admin:name')}*`,
                            field: 'name',
                            id: 'study-overview-name',
                            cellEditor: EditableGridCellEditor.TEXT,
                            required: true,
                            disabled: false,
                            minWidth: 200,
                            show: true,
                            validateFn: (stateVal: StudyOverviewTableConfigurationColumnConfigurations) => {
                                setErrors((e) => ({
                                    ...e,
                                    [`name-${stateVal.order}`]: '',
                                }));
                            },
                        },
                        {
                            label: `${t('admin:type')}*`,
                            field: 'type',
                            id: 'study-overview-type',
                            cellEditor: EditableGridCellEditor.SELECT,
                            required: true,
                            disabled: false,
                            minWidth: 200,
                            show: true,
                            validateFn: (stateVal: StudyOverviewTableConfigurationColumnConfigurations) => {
                                setErrors((e) => ({
                                    ...e,
                                    [`type-${stateVal.order}`]: '',
                                }));
                            },
                            selectValues: [
                                {
                                    id: null,
                                    text: t('admin:selectValue'),
                                },
                                {
                                    id: OverviewColumnConfigurationEnumType.OVERVIEW,
                                    text: t('common:Overview'),
                                },
                                {
                                    id: OverviewColumnConfigurationEnumType.CALCULATED,
                                    text: t('common:Calculated'),
                                },
                                {
                                    id: OverviewColumnConfigurationEnumType.STATIC,
                                    text: t('admin:STATIC'),
                                },
                            ],
                        },
                        {
                            label: t('common:dataSource'),
                            field: 'dataSource',
                            id: 'study-overview-data-source',
                            cellEditor: EditableGridCellEditor.SELECT,
                            required: true,
                            disabled: false,
                            minWidth: 200,
                            show: true,
                            selectValues: seriesRowDataOptions,
                            validateFn: (stateVal: StudyOverviewTableConfigurationColumnConfigurations) => {
                                setErrors((e) => ({
                                    ...e,
                                    [`dataSource-${stateVal.order}`]: '',
                                }));
                            },
                        },
                        {
                            label: t('common:fields'),
                            field: 'fields',
                            id: 'study-overview-data-fields',
                            cellEditor: EditableGridCellEditor.SELECT,
                            required: true,
                            disabled: false,
                            minWidth: 200,
                            show: true,
                            selectValues: defineSelectLOVs,
                            validateFn: (stateVal: StudyOverviewTableConfigurationColumnConfigurations) => {
                                setErrors((e) => ({
                                    ...e,
                                    [`fields-${stateVal.order}`]: '',
                                }));
                            },
                        },
                        {
                            label: `${t('admin:calculation')}`,
                            field: 'calculation',
                            id: 'study-overview-calculation',
                            cellEditor: EditableGridCellEditor.SELECT,
                            required: true,
                            disabled: false,
                            minWidth: 200,
                            show: true,
                            selectValues: defineSelectLOVs,
                            validateFn: (stateVal: StudyOverviewTableConfigurationColumnConfigurations) => {
                                setErrors((e) => ({
                                    ...e,
                                    [`calculation-${stateVal.order}`]: '',
                                }));
                            },
                        },
                    ]}
                    title={t('common:columns')}
                    extraMenuItems={extraMenuItems}
                    disableDeleteSingle
                    setState={(columnConfigurations: StudyOverviewTableConfigurationColumnConfigurations) => {
                        disabledSaveUndoButtons.setFalse();
                        setState({
                            ...state,
                            [currentOverviewConfigurationType]: {
                                ...state[currentOverviewConfigurationType],
                                columnConfigurations,
                            },
                        });
                    }}
                    disabledFields={disabledFields}
                    defineDisabledFields={defineDisabledFields}
                    errors={errors}
                    setErrors={setErrors}
                    lineHeight="19px"
                />
                {!isStudy && (
                    <RowOrder
                        selectedValue={state.studySiteOverviewTableConfigurationInputType?.rowOrder}
                        onSelect={(e: { target: { value: string } }) => {
                            disabledSaveUndoButtons.setFalse();
                            setState({
                                ...state,
                                studySiteOverviewTableConfigurationInputType: {
                                    ...state.studySiteOverviewTableConfigurationInputType,
                                    rowOrder: e.target.value,
                                },
                            });
                        }}
                    />
                )}
                <PanelsOverviewGrid
                    study={study}
                    state={state}
                    setState={setState}
                    disabledSaveUndoButtons={disabledSaveUndoButtons}
                    title={isStudy ? t('common:rows') : t('cm:panels')}
                    isStudy={isStudy}
                />
            </div>
        </div>
    );
};

export default EditStudySiteOverviewConfig;
